import * as tracking from 'dibs-tracking';
import { initializeTracking } from 'dibs-buyer-layout/src/entries/index';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import {
    getParamFromUrl,
    getCheckoutTrigger,
    isNegotiableOrder,
    isUserComesFromAcceptedOffer,
} from './checkoutRedirects';
import { PANES, LABELS } from '../constants/checkoutConstants';

const { STEP_INTERACTION_ENTRY, STEP_INTERACTION_SHIPPING_COUNTRY_CHANGED } =
    tracking.stepInteractionConstants;
const AUTH = authModalLoader.constants;
const FULL_ACCOUNT = 'full account';
let isUserInteractionTracked = false;
let hasTrackedEntryAction = false;
let hasTrackedPageView = false;
let initialPaneStorage;
let isPrivateOfferStorage;

export function storeEntryValues({ initialPane, isPrivateOffer }) {
    initialPaneStorage = initialPane;
    isPrivateOfferStorage = isPrivateOffer;
}

export function initializeDataLayer(environment) {
    tracking.resetGtmTrackingInSamePageView();
    initializeTracking(environment);
}

export function trackPageViewOnce(additional) {
    if (!hasTrackedPageView) {
        const params = new URLSearchParams(document.location.search);
        params.delete('message');
        tracking.trackPageview({
            url: `${document.location.pathname}?${params.toString()}`,
            additional,
        });

        hasTrackedPageView = true;
    }
}

export function trackPurchaseSource({
    action,
    label,
    isInteractiveEvent,
    eventName,
    interaction_type,
    step_interaction_name,
}) {
    tracking.trackEvent({
        category: 'purchase click',
        action,
        label,
        isInteractiveEvent,
        eventName,
        interaction_type,
        step_interaction_name,
    });
}

export function trackAddToCart(product) {
    tracking.trackEcommerce({
        type: 'addToCart',
        products: [product],
    });
}

export function trackAuthModalRegistration() {
    tracking.trackEvent({
        category: 'registration',
        action: 'registration complete',
        label: isNegotiableOrder() ? 'make offer' : 'purchase',
        eventName: tracking.eventNameConstants.EVENT_SIGN_UP,
        step_interaction_name: tracking.stepInteractionConstants.STEP_INTERACTION_COMPLETE,
        interaction_type: tracking.interactionTypeConstants.INTERACTION_TYPE_USER_REGISTRATION,
    });
}

export function trackAuthModalLogin() {
    tracking.trackEvent({
        category: 'login',
        action: 'login complete',
        label: isNegotiableOrder() ? 'make offer' : 'purchase',
        eventName: tracking.eventNameConstants.EVENT_LOGIN,
        step_interaction_name: tracking.stepInteractionConstants.STEP_INTERACTION_COMPLETE,
        interaction_type: tracking.interactionTypeConstants.INTERACTION_TYPE_USER_LOGIN,
    });
}

export function trackProceedToCheckout({ modal, flow }) {
    let action;
    switch (modal) {
        case AUTH.LOGIN:
        case AUTH.STALE_LOGIN_CHECKOUT_FLOW:
        case AUTH.STALE_LOGIN_EMAIL_ONLY_CHECKOUT_FLOW:
            action = flow === AUTH.AUTH ? 'login' : 'verification';
            break;
        case AUTH.REQUEST_PASSWORD_EMAIL:
            action = 'request password email';
            break;
        default:
            action = modal;
    }
    tracking.trackEvent({
        category: action.replace('-', ' '),
        action: `${action} form interaction`,
        label: 'click on register link - proceed to checkout',
    });
}

export function trackEmbeddedRegistration() {
    let label;
    if (isPrivateOfferStorage) {
        label = 'private offer';
    } else if (isNegotiableOrder()) {
        label = 'offer';
    } else {
        label = 'order';
    }
    tracking.trackEvent(
        {
            category: 'registration',
            action: 'registration entry',
            label,
            eventName: tracking.eventNameConstants.EVENT_SIGN_UP,
            step_interaction_name: tracking.stepInteractionConstants.STEP_INTERACTION_ENTRY,
            interaction_type: tracking.interactionTypeConstants.INTERACTION_TYPE_USER_REGISTRATION,
        },
        null
    );
}

export function trackLogin({ label, additional = {} }) {
    tracking.trackEvent(
        {
            category: 'registration',
            action: 'registration form interaction',
            label,
            isInteractiveEvent: true,
            additional: {
                ...additional,
                currentAccountType: FULL_ACCOUNT,
            },
        },
        null
    );
}

export function trackAction({
    action,
    step,
    isInteractiveEvent,
    paymentType,
    eventNameValue,
    stepInteractionValue,
    stepValue,
}) {
    const trigger = getCheckoutTrigger();
    const label = [];
    const triggerType = getParamFromUrl('triggerType');
    let filteredEventNameValue = eventNameValue;
    let stepTypeValue = stepValue;
    let entryValue;
    let triggerValue;

    if (initialPaneStorage) {
        label.push(`entry=${initialPaneStorage}`);
    }
    if (triggerType) {
        label.push(`trigger=${triggerType}`);
        triggerValue = triggerType;
    } else if (trigger) {
        const triggers = [trigger];

        if (isNegotiableOrder()) {
            triggers.push('make offer');
        } else if (getParamFromUrl('fullPrice') === 'true') {
            triggers.push('purchase at full price');
        } else if (isUserComesFromAcceptedOffer()) {
            triggers.push('accept offer');
        } else {
            triggers.push('purchase');
        }
        label.push(`trigger=${triggers.join('-')}`);
        triggerValue = triggers.join('-');
    }
    if (step) {
        label.push(`section=${step}`);

        //Add GA4 variables eventName and step_type where appropriate
        const eventDictionary = {
            [PANES.PANE_SHIPPING]: { eventName: 'add_shipping_info', stepType: 'shipping address' },
            [PANES.PANE_DELIVERY]: { eventName: 'add_shipping_info', stepType: 'shipping method' },
            [PANES.PANE_PAYMENT]: { eventName: 'add_payment_info', stepType: 'payment method' },
            [PANES.PANE_REVIEW]: { eventName: 'order_review', stepType: 'order review' },
        };

        const filteredStepType = eventDictionary[step]?.stepType;

        //Determine eventName and step_type if not passed in as variables and event tracker is not for submitting the order
        if (!eventNameValue && action !== 'complete') {
            filteredEventNameValue = eventDictionary[step]?.eventName;
            stepTypeValue = filteredStepType;
        }
        //For when checkout starts  add step_type
        if (eventNameValue === 'begin_checkout' && action !== 'complete') {
            stepTypeValue = filteredStepType;
        }

        //Populate entry values correlating with stepType when tracking event is for checkout flow entry
        if (eventNameValue === 'begin_checkout') {
            entryValue = stepTypeValue;
        }
    }
    if (paymentType) {
        label.push(`payment=${paymentType}`);
    }

    label.push('type=full');

    tracking.trackEvent({
        category: 'checkout',
        action: `checkout ${action}`,
        interaction_type: 'checkout',
        label: label.join('|'),
        isInteractiveEvent,
        eventName: filteredEventNameValue,
        step_interaction_name: stepInteractionValue,
        step_type: stepTypeValue,
        additional: {
            entry: entryValue,
            trigger: triggerValue,
        },
    });
}

export function trackPaneStep({ step, paneName, product }) {
    tracking.trackEcommerce({
        type: 'checkout',
        actionField: { step },
        products: [product],
    });
    if (paneName) {
        trackAction({
            action: 'step initiated',
            step: paneName,
            isInteractiveEvent: false,
            stepInteractionValue: STEP_INTERACTION_ENTRY,
        });
    }
}

export function trackEntryActionOnce({ initialPane }) {
    if (!hasTrackedEntryAction) {
        hasTrackedEntryAction = true;
        trackAction({
            action: 'entry',
            step: initialPane,
            isInteractiveEvent: false,
            eventNameValue: 'begin_checkout',
            stepInteractionValue: STEP_INTERACTION_ENTRY,
        });
    }
}

export function trackUserInteractionOnce() {
    if (!isUserInteractionTracked) {
        isUserInteractionTracked = true;
        trackAction({ action: 'form initiated', isInteractiveEvent: false });
    }
}

export function trackPaneChange({ step, paneName, product }) {
    trackUserInteractionOnce();
    trackPaneStep({ step, paneName, product });
}

export function trackCompletedStep(step) {
    trackUserInteractionOnce();
    trackAction({
        action: 'saved',
        step,
        isInteractiveEvent: false,
        stepInteractionValue: 'complete',
    });
}

export function trackStepBreadcrumbs(step) {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout step breadcrumb click',
        label: LABELS[step],
        event: 'e_ga_event',
        isInteractiveEvent: true,
        eventName: tracking.eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'checkout step breadcrumb click',
        step_interaction_name: LABELS[step],
    });
}

export function trackFormInteraction(label) {
    trackUserInteractionOnce();
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label,
        isInteractiveEvent: true,
    });
}

export function trackFormAction(label) {
    trackUserInteractionOnce();
    tracking.trackEvent({
        category: 'checkout form actions',
        action: isNegotiableOrder() ? 'offer' : 'order',
        label,
    });
}

export function trackFormError({ category = 'checkout', step = '', field = '', error = {} }) {
    trackUserInteractionOnce();
    tracking.trackFormError({
        action: [category, step, field.toLowerCase()].join('|'),
        label: (error.defaultMessage || '').toLowerCase(),
    });
}

export function trackFormErrors({ category = 'checkout', step = '', errors = {} }) {
    trackUserInteractionOnce();
    Object.keys(errors).forEach(field => {
        const error = errors[field];
        if (error) {
            trackFormError({ category, step, field, error });
        }
    });
}

export function trackTaxExempt({ label }) {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label,
    });
}

export function trackSidemark() {
    tracking.trackEvent({
        category: 'item invoice',
        action: 'checkout',
        label: 'sidemark updated',
    });
}

export function trackServiceError({ action, label }) {
    trackUserInteractionOnce();
    tracking.trackFormError({ action, label });
}

export function trackExistingUserError() {
    tracking.trackEvent({
        category: 'form error',
        action: 'registration|checkout form|email',
        label: 'existing email',
    });
}

export function trackSummaryExpanded(isExpanding) {
    if (isExpanding) {
        tracking.trackEvent({
            category: 'checkout',
            action: 'checkout form interaction',
            label: 'order summary section expanded',
        });
    }
}

export function trackZipCodeUpdate() {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label: 'order summary zip code updated',
    });
}

export function trackCountryChange() {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label: 'order summary country updated',
    });
}

export function trackPromoCodeSubmit(event, promoCode, { isSuccessful, source }) {
    const category = source ? 'checkout' : 'promo interaction';
    const action = isSuccessful ? 'complete' : 'error';
    const actionSource = source || 'Payment Method';

    tracking.trackEvent(
        {
            category,
            action: `Promo Code ${actionSource} Submit - ${action}`,
            label: promoCode,
            noninteraction: 1,
            additional: {
                promoCode: isSuccessful ? promoCode : undefined,
            },
        },
        event
    );
}

export function trackKlarnaInteraction(label, { isInteractive }) {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label,
        isInteractiveEvent: isInteractive,
    });
}

export function trackGuestEmailError(label) {
    tracking.trackEvent({
        category: 'checkout',
        action: 'guest login error',
        label,
        interaction_type: 'user login',
        step_interaction_name: label,
        isInteractiveEvent: false,
    });
}

export function trackExistingUserGuestCheckout() {
    tracking.trackEvent({
        category: 'checkout',
        action: 'existing user guest checkout',
        label: 'guest checkout',
        isInteractiveEvent: false,
    });
}

export function trackGuestEmailRegistration() {
    tracking.trackEvent({
        category: 'registration',
        action: 'registration complete',
        label: 'guest checkout',
        isInteractiveEvent: false,
    });
}

export function trackLabel(step) {
    const trigger = getCheckoutTrigger();
    const label = [];
    const triggerType = getParamFromUrl('triggerType');

    if (initialPaneStorage) {
        label.push(`entry=${initialPaneStorage}`);
    }
    if (triggerType) {
        label.push(`trigger=${triggerType}`);
    } else if (trigger) {
        const triggers = [trigger];

        if (isNegotiableOrder()) {
            triggers.push('make offer');
        } else if (getParamFromUrl('fullPrice') === 'true') {
            triggers.push('purchase at full price');
        } else if (isUserComesFromAcceptedOffer()) {
            triggers.push('accept offer');
        } else {
            triggers.push('purchase');
        }
        label.push(`trigger=${triggers.join('-')}`);
    }
    if (step) {
        label.push(`section=${step}`);
    }

    label.push('type=full');

    return label.join('|');
}

export function trackBuyerChangedCountryAndPriceBookNameMismatched({
    priceDifference,
    newPriceBookName,
    originalPriceBookName,
}) {
    let itemPriceChange = '';
    if (priceDifference > 0) {
        itemPriceChange = 'up';
    } else if (priceDifference < 0) {
        itemPriceChange = 'down';
    } else {
        itemPriceChange = 'unchanged';
    }

    tracking.trackEvent({
        category: 'checkout',
        action: 'ITE country changed',
        label: `item price ${itemPriceChange} - ${newPriceBookName}|${originalPriceBookName} pricebook`,
        isInteractiveEvent: true,
        eventName: tracking.eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        step_interaction_name: STEP_INTERACTION_SHIPPING_COUNTRY_CHANGED,
        interaction_type: tracking.interactionTypeConstants.INTERACTION_TYPE_CHECKOUT,
    });
}
